/* The retry key for a staff command (assign, start work, change priority).
 *
 * Same idea as operationKey.js for "create a ticket": the server saves a command once per key,
 * so pressing the same button again after an answer got lost must carry the SAME key (the server
 * answers with the first result), and a DIFFERENT command must carry a NEW key (the server
 * refuses a key reused for different content).
 *
 * `memory` is what the last UNRESOLVED attempt looked like, or null. The caller clears it when the
 * server gave a definite answer (saved, or refused for a reason). Pure on purpose: no React, no
 * storage. `makeId` is injectable for tests. */

const defaultId = () => crypto.randomUUID();

function canonical(value) {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

/** intent: { command, args, expectedVersion }. Returns { key, memory } - keep `memory` for the next press. */
export function keyForCommand(memory, intent, makeId = defaultId) {
  const signature = canonical({ command: intent.command, args: intent.args || {}, expectedVersion: intent.expectedVersion });
  const key = memory && memory.signature === signature ? memory.key : makeId();
  return { key, memory: { signature, key } };
}

/* The sentence a person sees when a command did not come back as saved. A refusal carries the
 * server's own plain words; an unknown outcome never says "not saved". */
export function wordsForCommandError(error) {
  if (error && error.outcome === 'rejected') {
    return error.code === 'STALE'
      ? 'Someone else changed this ticket first. It has been reloaded - look at it and try again.'
      : error.message;
  }
  return 'We could not confirm that this saved. Look at the ticket, then press the button again - it will not be done twice.';
}
